/**
 * ADOS-2 Module 4 Items Configuration
 *
 * Maps the 26 ADOS-2 Module 4 items (from the full 30) that are relevant to
 * the DSM-5 criteria for Autism Spectrum Disorder. Each item belongs to one of
 * four ADOS domains (A: Language & Communication, B: Reciprocal Social
 * Interaction, C: Imagination, D: Stereotyped Behaviours & Restricted Interests).
 *
 * The `dsmCriteria` array on each item indicates which DSM-5 criterion/criteria
 * it contributes evidence toward. This drives which ADOS items appear on each
 * criterion page during the legacy "Map to ADOS" workflow.
 *
 * `defaultTag` (on ADOS_ALL_ITEMS) is the Criterion Mapping tag auto-applied to
 * notes taken against that item during ADOS-informed Coding. It follows the
 * TOC Autism clinician mapping (one primary tag per item). Values are ids from
 * OBS_NOTE_TAG_OPTIONS (A1–B4, or 'other' for catch-all behaviours).
 *
 * Traffic-light scoring:
 *   0 (green)  — No evidence of abnormality
 *   1 (amber)  — Some evidence / borderline
 *   2 (red)    — Clear evidence of abnormality
 */

export const ADOS_ITEMS = [
  // ── Domain A: Language & Communication ──────────────────────────────────
  { id: 'A2',  domain: 'A', title: 'Speech Abnormalities',                    dsmCriteria: ['B1'] },
  { id: 'A3',  domain: 'A', title: 'Echolalia',                               dsmCriteria: ['B1'] },
  { id: 'A4',  domain: 'A', title: 'Stereotyped/Idiosyncratic Language',      dsmCriteria: ['B1'] },
  { id: 'A5',  domain: 'A', title: 'Offers Information',                      dsmCriteria: ['A1'] },
  { id: 'A6',  domain: 'A', title: 'Asks for Information',                    dsmCriteria: ['A1'] },
  { id: 'A7',  domain: 'A', title: 'Reporting of Events',                     dsmCriteria: ['A1'] },
  { id: 'A8',  domain: 'A', title: 'Conversation',                            dsmCriteria: ['A1'] },
  { id: 'A9',  domain: 'A', title: 'Descriptive Gestures',                    dsmCriteria: ['A2'] },
  { id: 'A10', domain: 'A', title: 'Emphatic Gestures',                       dsmCriteria: ['A2'] },

  // ── Domain B: Reciprocal Social Interaction ─────────────────────────────
  { id: 'B1',  domain: 'B', title: 'Eye Contact',                             dsmCriteria: ['A2'] },
  { id: 'B2',  domain: 'B', title: 'Facial Expression',                       dsmCriteria: ['A2'] },
  { id: 'B3',  domain: 'B', title: 'Integration of Verbal and Non-Verbal Communication', dsmCriteria: ['A2'] },
  { id: 'B5',  domain: 'B', title: 'Communication of Own Affect',             dsmCriteria: ['A1'] },
  { id: 'B6',  domain: 'B', title: 'Comments on Others\' Emotions',           dsmCriteria: ['A3'] },
  { id: 'B7',  domain: 'B', title: 'Insight into Social Relationships',       dsmCriteria: ['A3'] },
  { id: 'B8',  domain: 'B', title: 'Responsibility',                          dsmCriteria: ['A3'] },
  { id: 'B9',  domain: 'B', title: 'Quality of Social Overtures',             dsmCriteria: ['A1', 'A3'] },
  { id: 'B10', domain: 'B', title: 'Amount of Social Overtures',              dsmCriteria: ['A1'] },
  { id: 'B11', domain: 'B', title: 'Social Responses',                        dsmCriteria: ['A1', 'A3'] },
  { id: 'B12', domain: 'B', title: 'Reciprocal Social Communication',         dsmCriteria: ['A1'] },
  { id: 'B13', domain: 'B', title: 'Rapport',                                 dsmCriteria: ['A1'] },

  // ── Domain C: Imagination ───────────────────────────────────────────────
  { id: 'C1',  domain: 'C', title: 'Imagination/Creativity',                  dsmCriteria: ['A3'] },

  // ── Domain D: Stereotyped Behaviours & Restricted Interests ─────────────
  { id: 'D1',  domain: 'D', title: 'Sensory Interests',                       dsmCriteria: ['B4'] },
  { id: 'D2',  domain: 'D', title: 'Hand/Finger Mannerisms',                  dsmCriteria: ['B1'] },
  { id: 'D4',  domain: 'D', title: 'Excessive Interests/Behaviours',          dsmCriteria: ['B3'] },
  { id: 'D5',  domain: 'D', title: 'Compulsions/Rituals',                     dsmCriteria: ['B2'] },
];

/**
 * Returns the subset of ADOS items that are relevant for a given DSM criterion.
 * Used to render the ADOS mapping table on each criterion's page.
 */
export function getAdosItemsForCriterion(criterionId) {
  return ADOS_ITEMS.filter(item => item.dsmCriteria.includes(criterionId));
}

/**
 * Complete ADOS-2 Module 4 item list (all 30 items) for the flat scoring
 * table that presents all items grouped by domain.
 * Includes items that don't map cleanly to a single DSM criterion.
 *
 * `defaultTag` is the OBS note-tag id auto-applied when a clinician takes a
 * bullet note against this item. Mapping follows TOC Autism clinical guidance
 * (e.g. A2 Speech Abnormalities → DSM A2; D3 / E-domain → Other Behaviours).
 */
export const ADOS_ALL_ITEMS = [
  // ── Domain A: Language & Communication (10 items) ─────────────────────
  { id: 'A1',  domain: 'A', title: 'Overall Level of Non-Echoed Language',              defaultTag: 'A1' },
  { id: 'A2',  domain: 'A', title: 'Speech Abnormalities',                             defaultTag: 'A2' },
  { id: 'A3',  domain: 'A', title: 'Echolalia',                                        defaultTag: 'B1' },
  { id: 'A4',  domain: 'A', title: 'Stereotyped/Idiosyncratic Language',               defaultTag: 'B1' },
  { id: 'A5',  domain: 'A', title: 'Offers Information',                               defaultTag: 'A1' },
  { id: 'A6',  domain: 'A', title: 'Asks for Information',                             defaultTag: 'A1' },
  { id: 'A7',  domain: 'A', title: 'Reporting of Events',                              defaultTag: 'A1' },
  { id: 'A8',  domain: 'A', title: 'Conversation',                                     defaultTag: 'A1' },
  { id: 'A9',  domain: 'A', title: 'Descriptive Gestures',                             defaultTag: 'A2' },
  { id: 'A10', domain: 'A', title: 'Emphatic Gestures',                                defaultTag: 'A2' },

  // ── Domain B: Reciprocal Social Interaction (13 items) ────────────────
  { id: 'B1',  domain: 'B', title: 'Eye Contact',                                      defaultTag: 'A2' },
  { id: 'B2',  domain: 'B', title: 'Facial Expression',                                defaultTag: 'A2' },
  { id: 'B3',  domain: 'B', title: 'Integration of Verbal and Non-Verbal Communication', defaultTag: 'A2' },
  { id: 'B4',  domain: 'B', title: 'Shared Enjoyment in Interaction',                  defaultTag: 'A1' },
  { id: 'B5',  domain: 'B', title: 'Communication of Own Affect',                      defaultTag: 'A1' },
  { id: 'B6',  domain: 'B', title: 'Comments on Others\' Emotions',                    defaultTag: 'A1' },
  { id: 'B7',  domain: 'B', title: 'Insight into Social Relationships',                defaultTag: 'A3' },
  { id: 'B8',  domain: 'B', title: 'Responsibility',                                   defaultTag: 'A3' },
  { id: 'B9',  domain: 'B', title: 'Quality of Social Overtures',                      defaultTag: 'A1' },
  { id: 'B10', domain: 'B', title: 'Amount of Social Overtures',                       defaultTag: 'A1' },
  { id: 'B11', domain: 'B', title: 'Social Responses',                                 defaultTag: 'A1' },
  { id: 'B12', domain: 'B', title: 'Reciprocal Social Communication',                  defaultTag: 'A1' },
  { id: 'B13', domain: 'B', title: 'Rapport',                                          defaultTag: 'A3' },

  // ── Domain C: Imagination (1 item) ────────────────────────────────────
  { id: 'C1',  domain: 'C', title: 'Imagination/Creativity',                           defaultTag: 'B2' },

  // ── Domain D: Stereotyped Behaviours & Restricted Interests (5 items) ─
  { id: 'D1',  domain: 'D', title: 'Sensory Interests',                                defaultTag: 'B4' },
  { id: 'D2',  domain: 'D', title: 'Hand/Finger Mannerisms',                           defaultTag: 'B1' },
  { id: 'D3',  domain: 'D', title: 'Self-Injurious Behaviour',                         defaultTag: 'other' },
  { id: 'D4',  domain: 'D', title: 'Excessive Interests/Behaviours',                   defaultTag: 'B3' },
  { id: 'D5',  domain: 'D', title: 'Compulsions/Rituals',                              defaultTag: 'B2' },

  // ── Domain E: Other Behaviours (3 items) ──────────────────────────────
  { id: 'E1',  domain: 'E', title: 'Overactivity/Agitation',                           defaultTag: 'other' },
  { id: 'E2',  domain: 'E', title: 'Tantrums, Aggression, Negative or Disruptive Behaviour', defaultTag: 'other' },
  { id: 'E3',  domain: 'E', title: 'Anxiety',                                          defaultTag: 'other' },
];

/**
 * ADOS domain labels for grouping items in the table header.
 */
export const ADOS_DOMAINS = {
  A: 'Language & Communication',
  B: 'Reciprocal Social Interaction',
  C: 'Imagination',
  D: 'Stereotyped Behaviours & Restricted Interests',
  E: 'Other Behaviours',
};
